
import { getIncidents, getCatalogTasks } from '@/data/jsonImports';
import { toast } from 'sonner';

export interface JsonData {
  [key: string]: any[];
}

/**
 * Normalizes ticket fields for consistency across data sources
 */
export const normalizeTicketFields = (ticket: any): any => {
  const normalized = {...ticket};
  
  const fieldMappings: Record<string, string[]> = {
    'number': ['incident_number', 'task_number', 'Number', 'ticket_number', 'ID'],
    'priority': ['Priority', 'urgency', 'Urgency', 'severity', 'Severity'],
    'state': ['status', 'Status', 'State', 'ticket_status'],
    'service': ['Service', 'product', 'Product', 'application', 'Application'],
    'assigned_to': ['AssignedTo', 'Assigned To', 'assigned to', 'Assigned to', 'owner', 'Owner'],
    'short_description': ['ShortDescription', 'Short Description', 'Short description', 'title', 'Title', 'summary', 'Summary'],
    'description': ['Description', 'details', 'Details', 'comment', 'Comment'],
    'opened': ['opened_at', 'created', 'created_at', 'OpenedAt', 'CreatedAt', 'date', 'Date', 'Opened'],
    'closed': ['closed_at', 'completed', 'completed_at', 'ClosedAt', 'CompletedAt', 'Closed'],
    'assignment_group': ['AssignmentGroup', 'Assignment Group', 'group', 'Group', 'team', 'Team'],
  };
  
  Object.keys(fieldMappings).forEach(standardKey => {
    if (!normalized[standardKey]) {
      const alternateKeys = fieldMappings[standardKey];
      for (const altKey of alternateKeys) {
        if (normalized[altKey]) {
          normalized[standardKey] = normalized[altKey];
          break;
        }
      }
    }
    
    if (!normalized[standardKey]) {
      normalized[standardKey] = '';
    }
  });
  
  // Add API name field if it can be extracted from the description
  if (!normalized.api_name) {
    normalized.api_name = extractApiName(normalized.short_description || '') || 
                        extractApiName(normalized.description || '') ||
                        extractApiName(normalized.service || '');
  }
  
  // Generate camelCase and Title Case versions of fields for compatibility
  Object.keys(normalized).forEach(key => {
    const camelCase = key.replace(/(?:^|_)([a-z])/g, (_, p1) => p1.toUpperCase()).replace(/^./, c => c.toLowerCase());
    if (camelCase !== key && !normalized[camelCase]) {
      normalized[camelCase] = normalized[key];
    }
    
    const titleCase = key.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    if (titleCase !== key && !normalized[titleCase]) {
      normalized[titleCase] = normalized[key];
    }
  });
  
  return normalized;
};

/**
 * Extracts API name from text
 */
export const extractApiName = (text: string): string | null => {
  if (!text || typeof text !== 'string') return null;
  
  // Match patterns like xxx-api, xxx-sys, xxx-prc, xxx-exp
  const apiRegex = /\b([a-z0-9-]+[-](?:api|sys|prc|exp))\b/i;
  const systemRegex = /\b([a-z0-9-]+[-](?:sys|prc|exp))\b/i;
  const eamRegex = /\bEAM\b/i;
  
  // Check for API pattern
  const apiMatch = text.match(apiRegex);
  if (apiMatch) return apiMatch[0];
  
  // Check for system pattern
  const systemMatch = text.match(systemRegex);
  if (systemMatch) return systemMatch[0];
  
  // Check for EAM
  const eamMatch = text.match(eamRegex);
  if (eamMatch) return 'EAM-system';
  
  return null;
};

/**
 * Loads JSON data from static files
 */
export const loadJsonData = async (): Promise<JsonData> => {
  try {
    console.log("🔍 Loading JSON data from static files");
    
    const incidentsData = getIncidents();
    const catalogTasksData = getCatalogTasks();
    
    // Check if we have valid JSON data
    const hasIncidents = Array.isArray(incidentsData) && incidentsData.length > 0;
    const hasCatalogTasks = Array.isArray(catalogTasksData) && catalogTasksData.length > 0;
    
    console.log(`📊 JSON Data check - Incidents: ${hasIncidents ? incidentsData.length : 'None'}, Catalog Tasks: ${hasCatalogTasks ? catalogTasksData.length : 'None'}`);
    
    if (hasIncidents) {
      console.log("📊 Sample incident:", incidentsData[0]);
    }
    
    if (hasCatalogTasks) {
      console.log("📊 Sample catalog task:", catalogTasksData[0]);
    }
    
    if (!hasIncidents && !hasCatalogTasks) {
      console.warn("⚠️ No valid JSON data found, using mock data instead");
      toast.warning("No valid JSON data found, using mock data instead");
      return createMockData();
    }
    
    // Process the incidents and catalog tasks to ensure consistent field names
    const processedIncidents = Array.isArray(incidentsData) 
      ? incidentsData.map(normalizeTicketFields)
      : [];
    
    const processedCatalogTasks = Array.isArray(catalogTasksData) 
      ? catalogTasksData.map(normalizeTicketFields)
      : [];
    
    console.log(`✅ Processed ${processedIncidents.length} incidents and ${processedCatalogTasks.length} catalog tasks`);
    
    // Display success message with counts
    toast.success(`Loaded ${processedIncidents.length} incidents and ${processedCatalogTasks.length} catalog tasks from JSON`);
    
    return {
      'Incidents': processedIncidents,
      'Catalog Tasks': processedCatalogTasks
    };
  } catch (error) {
    console.error('❌ Error loading JSON data:', error);
    console.log("🔄 Falling back to mock data");
    toast.error("Error loading JSON data, using mock data instead");
    return createMockData();
  }
};

/**
 * Creates mock data for testing
 */
function createMockData(): { [key: string]: any[] } {
  console.log("Creating mock incident and catalog task data");
  
  // Create some mock incidents
  const mockIncidents = Array.from({ length: 20 }, (_, i) => ({
    number: `INC${10000 + i}`,
    short_description: `Issue with ${['user-api', 'payment-api', 'auth-api'][i % 3]} - ${['timeout', 'data error', 'connection failed'][i % 3]}`,
    description: `Detailed description for incident ${i + 1}`,
    priority: ['1', '2', '3', '4'][i % 4],
    state: ['Open', 'In Progress', 'Resolved', 'Closed'][i % 4],
    opened: new Date(2025, 3, i % 30 + 1).toISOString(),
    closed: i % 3 === 0 ? undefined : new Date(2025, 3, (i % 30) + 3).toISOString(),
    assignment_group: ['IT Support', 'Development', 'Operations'][i % 3],
    assigned_to: `User ${i % 5 + 1}`,
    api_name: ['user-api', 'payment-api', 'auth-api'][i % 3]
  }));
  
  // Create some mock catalog tasks
  const mockCatalogTasks = Array.from({ length: 15 }, (_, i) => ({
    number: `TASK${20000 + i}`,
    short_description: `Deploy ${['user-api', 'payment-api', 'auth-api'][i % 3]} version ${i % 5}.${i % 10}`,
    description: `Detailed description for task ${i + 1}`,
    priority: ['1', '2', '3', '4'][i % 4],
    state: ['Open', 'In Progress', 'Complete', 'Closed'][i % 4],
    opened: new Date(2025, 3, i % 30 + 1).toISOString(),
    closed: i % 4 === 0 ? undefined : new Date(2025, 3, (i % 30) + 2).toISOString(),
    assignment_group: ['Development', 'Release Management', 'Operations'][i % 3],
    assigned_to: `User ${i % 4 + 1}`,
    api_name: ['user-api', 'payment-api', 'auth-api'][i % 3]
  }));
  
  console.log("Created mock data:", {
    incidents: mockIncidents.length,
    catalogTasks: mockCatalogTasks.length
  });
  
  return {
    'Incidents': mockIncidents,
    'Catalog Tasks': mockCatalogTasks
  };
}
